import React, { useState, useCallback, useEffect } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  NumberInput,
  required,
  TopToolbar,
  ListButton,
  useDataProvider,
  useRedirect,
  useNotify,
} from 'react-admin';
import {
  Card,
  Typography,
  Box,
  Divider,
  Alert,
  Grid2,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  DataObject as VariableIcon,
  List as ListIcon,
} from '@mui/icons-material';
import HierarchicalFilter, {
  HierarchicalFilterSelections,
} from '../../components/HierarchicalFilter';

const VariableCreateActions = () => (
  <TopToolbar>
    <ListButton icon={<ListIcon />} />
  </TopToolbar>
);

const VariablesCreate = () => {
  // Estado para almacenar el parámetro preseleccionado
  const [preselectedTableId, setPreselectedTableId] = useState<string | null>(
    null
  );

  // Estado para el filtro jerárquico
  const [filterValues, setFilterValues] =
    useState<HierarchicalFilterSelections>({});

  // Estado para el diálogo de confirmación de duplicados
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [pendingData, setPendingData] = useState<any>(null);
  const [duplicateMessage, setDuplicateMessage] = useState('');

  // Callback memoizado para el cambio de filtro
  const handleFilterChange = useCallback(
    (selections: HierarchicalFilterSelections) => {
      setFilterValues(selections);
    },
    []
  );

  // Obtener parámetros de la URL usando window.location de forma segura
  useEffect(() => {
    // Para hash routing, necesitamos obtener los parámetros del hash
    const hash = window.location.hash;
    const queryIndex = hash.indexOf('?');
    if (queryIndex !== -1) {
      const queryString = hash.substring(queryIndex + 1);
      const urlParams = new URLSearchParams(queryString);
      const tableId = urlParams.get('tableId');
      setPreselectedTableId(tableId);

      // Si hay una tabla preseleccionada, establecerla en el filtro
      if (tableId) {
        setFilterValues({ tableId: parseInt(tableId) });
      }
    }
  }, []);

  // Hooks para manejar la creación personalizada
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const notify = useNotify();

  // Función para crear la variable con ignoreWarnings opcional
  const createVariable = async (data: any, ignoreWarnings: boolean = false) => {
    const tableId =
      filterValues.tableId ||
      (preselectedTableId ? parseInt(preselectedTableId) : undefined);

    if (!tableId) {
      notify('Por favor selecciona una tabla antes de crear la variable', {
        type: 'error',
      });
      throw new Error('No se ha seleccionado una tabla');
    }

    // Payload según CreateVariableScadaDto
    const payload = {
      variableName: data.variableName,
      varType: 'SCADA',
      description: data.description || null,
      scadaReferenceName: data.scadaReferenceName,
      metricsInterval: data.metricsInterval > 0 ? data.metricsInterval : null,
      tableId,
    };

    console.log('Payload enviado:', payload, 'ignoreWarnings:', ignoreWarnings);

    // Usar dataProvider directamente para tener control sobre el error
    return dataProvider.create('variables', {
      data: payload,
      meta: { ignoreWarnings }
    });
  };

  // Función personalizada para manejar el envío del formulario
  const handleSave = async (data: any) => {
    try {
      await createVariable(data, false);

      notify('Variable SCADA creada exitosamente', { type: 'success' });
      redirect('/variables');
    } catch (error: any) {
      console.error('Error al crear variable:', error);

      // Verificar si el error es por duplicado de ScadaReferenceName
      const errorMessage = error?.body?.errors?.[0]?.description ||
                           error?.message ||
                           '';

      if (errorMessage.includes('Ya existe una variable SCADA con el nombre de referencia')) {
        // Mostrar diálogo de confirmación
        setDuplicateMessage(errorMessage);
        setPendingData(data);
        setShowDuplicateDialog(true);
      } else {
        notify(errorMessage || 'Error al crear la variable', { type: 'error' });
      }
    }
  };

  // Función para confirmar la creación con duplicado
  const handleConfirmDuplicate = async () => {
    setShowDuplicateDialog(false);

    try {
      await createVariable(pendingData, true);

      notify('Variable SCADA creada exitosamente', { type: 'success' });
      redirect('/variables');
    } catch (error: any) {
      console.error('Error al crear variable con ignoreWarnings:', error);
      const errorMessage = error?.body?.errors?.[0]?.description ||
                           error?.message ||
                           'Error al crear la variable';
      notify(errorMessage, { type: 'error' });
    }

    setPendingData(null);
    setDuplicateMessage('');
  };

  // Función para cancelar la creación con duplicado
  const handleCancelDuplicate = () => {
    setShowDuplicateDialog(false);
    setPendingData(null);
    setDuplicateMessage('');
  };

  return (
    <Create actions={<VariableCreateActions />} redirect={false}>
      <Box sx={{ padding: 3, margin: '0 auto' }}>
        <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
          <Box sx={{ p: 3 }}>
            <Box display='flex' alignItems='center' mb={3}>
              <VariableIcon
                sx={{ mr: 2, fontSize: 32, color: 'primary.main' }}
              />
              <Box>
                <Typography variant='h5' component='h1' fontWeight='bold'>
                  Crear Nueva Variable SCADA
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Agrega una nueva variable SCADA al sistema
                </Typography>
              </Box>
            </Box>

            {/* Filtro Jerárquico para seleccionar planta, zona y tabla */}
            <Card
              sx={{
                p: 3,
                mb: 3,
                boxShadow: 2,
                backgroundColor: 'background.default',
              }}
            >
              <Typography variant='h6' gutterBottom color='primary'>
                Seleccionar Planta, Zona y Tabla
              </Typography>
              <HierarchicalFilter
                levels={['plant', 'zone', 'table']}
                onChange={handleFilterChange}
                initialValues={filterValues}
                size='medium'
                useTableSelector={true}
              />

              {!filterValues.tableId && (
                <Alert severity='warning' sx={{ mt: 2 }}>
                  Debes seleccionar una planta, zona y tabla antes de crear la
                  variable
                </Alert>
              )}

              {filterValues.tableId && (
                <Alert severity='success' sx={{ mt: 2 }}>
                  ✓ Selección completada: {filterValues.plantName} →{' '}
                  {filterValues.zoneName} → {filterValues.tableName}
                </Alert>
              )}
            </Card>

            <SimpleForm
              onSubmit={handleSave}
              sx={{
                '& .RaSimpleForm-toolbar': {
                  marginTop: 3,
                  padding: 2,
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: 2,
                },
              }}
            >
              <Card
                sx={{
                  p: 3,
                  mb: 3,
                  boxShadow: 2,
                  borderRadius: 2,
                  width: '100%',
                  backgroundColor: 'background.default',
                }}
              >
                <Typography variant='h6' gutterBottom color='primary'>
                  Información de la Variable SCADA
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <TextInput
                  source='variableName'
                  label='Nombre de la Variable'
                  validate={[required()]}
                  fullWidth
                  sx={{ mb: 3 }}
                  disabled={!filterValues.tableId}
                  helperText='Nombre único para identificar la variable'
                />

                <TextInput
                  source='scadaReferenceName'
                  label='Referencia SCADA'
                  validate={[required()]}
                  fullWidth
                  sx={{ mb: 3 }}
                  disabled={!filterValues.tableId}
                  helperText='Nombre de referencia utilizado en el sistema SCADA'
                />

                <TextInput
                  source='description'
                  label='Descripción (Opcional)'
                  multiline
                  rows={3}
                  fullWidth
                  sx={{ mb: 3 }}
                  disabled={!filterValues.tableId}
                  helperText='Descripción detallada de la variable'
                />

                <NumberInput
                  source='metricsInterval'
                  label='Intervalo de Métricas (ms)'
                  fullWidth
                  sx={{ mb: 2 }}
                  disabled={!filterValues.tableId}
                  helperText='Intervalo en milisegundos para recolección de métricas (opcional)'
                />

                {/* Información sobre el tipo de variable */}
                <Alert severity='info' sx={{ mt: 2 }}>
                  <Typography variant='body2'>
                    <strong>Tipo de variable:</strong> SCADA (establecido
                    automáticamente)
                  </Typography>
                </Alert>
              </Card>

              {!filterValues.tableId && (
                <Alert severity='info'>
                  Completa la selección de planta, zona y tabla arriba para
                  habilitar los campos del formulario
                </Alert>
              )}
            </SimpleForm>
          </Box>
        </Card>
      </Box>

      {/* Diálogo de confirmación para duplicados */}
      <Dialog
        open={showDuplicateDialog}
        onClose={handleCancelDuplicate}
        aria-labelledby='duplicate-dialog-title'
        aria-describedby='duplicate-dialog-description'
      >
        <DialogTitle id='duplicate-dialog-title'>
          Variable SCADA Duplicada
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='duplicate-dialog-description'>
            {duplicateMessage}
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, fontWeight: 'bold' }}>
            ¿Deseas crear la variable de todas formas?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDuplicate} color='primary'>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDuplicate}
            color='warning'
            variant='contained'
            autoFocus
          >
            Crear de todas formas
          </Button>
        </DialogActions>
      </Dialog>
    </Create>
  );
};

export default VariablesCreate;
